import React, { useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import { ZoomIn as ZoomInIcon } from '@mui/icons-material';
import { useThumbnailLoader } from '../../PdfThumbnailRenderer';

export default function VirtualizedThumbnail({
  pageNum,
  generateThumbnail,
  chunkInfo,
  isSelected,
  isInSelectedChunk,
  onPageClick,
  onZoomClick,
  thumbnailRef,
  bookPageNum,
}) {
  const theme = useTheme();
  const { elementRef, thumbnail, isLoading } = useThumbnailLoader(pageNum, generateThumbnail);
  const setRefs = useCallback(
    (el) => {
      elementRef.current = el;
      if (thumbnailRef) thumbnailRef.current[pageNum] = el;
    },
    [elementRef, thumbnailRef, pageNum]
  );

  return (
    <Card
      ref={setRefs}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        border: isSelected
          ? `2px solid ${theme.palette.chunk.selectedBorder}`
          : isInSelectedChunk
          ? '1px solid #2e3440'
          : chunkInfo
          ? `1px solid ${theme.palette.chunk.inChunkBorder}`
          : `1px solid ${theme.palette.chunk.inChunk}`,
        bgcolor: isSelected
          ? theme.palette.chunk.selected
          : isInSelectedChunk
          ? theme.palette.chunk.selectedBorder
          : chunkInfo
          ? theme.palette.chunk.inChunk
          : theme.palette.background.paper,
      }}
      onClick={(e) => onPageClick(pageNum, e)}
    >
      <IconButton
        size="small"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          color: 'white',
          bgcolor: 'rgba(0,0,0,0.3)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onZoomClick(pageNum);
        }}
      >
        <ZoomInIcon fontSize="small" />
      </IconButton>

      {thumbnail ? (
        <img
          src={thumbnail}
          alt={`Page ${pageNum}`}
          style={{ width: '100%', height: 120, objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            height: 120,
            bgcolor: '#eceff4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {isLoading ? 'Loading...' : `Page ${pageNum}`}
          </Typography>
        </Box>
      )}
      <CardContent sx={{ p: 1.5 }}>
        <Typography variant="body2" align="center" color="#111" fontWeight="bold">
          Book: {bookPageNum}
        </Typography>
        <Typography variant="caption" align="center" color="text.secondary" display="block">
          (PDF: {pageNum})
        </Typography>
        {chunkInfo && (
          <Chip
            label={chunkInfo.name}
            size="small"
            sx={{
              mt: 0.5,
              width: '100%',
              fontSize: '0.7rem',
              height: '20px',
              bgcolor: '#f0f3f9ff',
              color: '#2e3440',
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
